import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import IntegrationAuthProvider from '../../../IntegrationAuthProvider/IntegrationAuthProvider'
import Loading from '../../../../molecules/Loading'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import SydButton from '../../../../commonDesign/Button'
import NotLinkedInline from '../../../WealthboxClientLink/NotLinkedInline'
import SmartComponentDisplayName from '../../SmartComponentDisplayName'
import useWealthboxListPreset from './useWealthboxListPreset'

const SIZE_STYLES = {
  sm: { rowPadding: '8px 0', fontSize: 'var(--font-size-sm)', minHeight: '120px' },
  md: { rowPadding: '10px 0', fontSize: 'var(--font-size-sm)', minHeight: '180px' },
  lg: { rowPadding: '12px 0', fontSize: 'var(--font-size-md)', minHeight: '240px' }
}

function WealthboxListInner ({ config, styleVariant: sizeVariant }) {
  const presetKey = config?.preset
  const [createOpen, setCreateOpen] = useState(false)
  const {
    items,
    limit,
    contactId,
    Content,
    isLoading,
    notLinked,
    title,
    icon
  } = useWealthboxListPreset(presetKey, config)

  const sizes = SIZE_STYLES[sizeVariant] || SIZE_STYLES.md
  const enableCreate = config?.enableCreate ?? false

  const handleOpenCreate = useCallback(() => setCreateOpen(true), [])
  const handleCloseCreate = useCallback(() => setCreateOpen(false), [])

  if (!presetKey) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        No preset configured for this list component.
      </div>
    )
  }

  if (isLoading) {
    return <Loading />
  }

  if (notLinked) {
    return (
      <FadeIn>
        <NotLinkedInline minHeight={sizes.minHeight} />
      </FadeIn>
    )
  }

  if (!Content) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        Unknown preset: {presetKey}
      </div>
    )
  }

  return (
    <FadeIn>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ marginBottom: '8px' }}>
          <SmartComponentDisplayName title={title} icon={icon} size={sizeVariant}>
            {enableCreate ? (
              <SydButton
                size='sm'
                variant='outline'
                icon='add'
                onClick={handleOpenCreate}
              >
                Create
              </SydButton>
            ) : null}
          </SmartComponentDisplayName>
        </div>

        <Content
          items={items}
          limit={limit}
          sizes={sizes}
          config={config}
          contactId={contactId}
          createOpen={createOpen}
          onCloseCreate={handleCloseCreate}
        />
      </div>
    </FadeIn>
  )
}

WealthboxListInner.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string
}

function WealthboxListRenderer ({ config, styleVariant, presentationKey, blueprint, displayName }) {
  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md

  return (
    <div style={{ minHeight: sizes.minHeight }}>
      <IntegrationAuthProvider integration='wealthbox' size='sm' loadingType='circular'>
        <WealthboxListInner
          config={config}
          styleVariant={styleVariant}
          presentationKey={presentationKey}
          blueprint={blueprint}
          displayName={displayName}
        />
      </IntegrationAuthProvider>
    </div>
  )
}

WealthboxListRenderer.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string,
  presentationKey: PropTypes.string,
  blueprint: PropTypes.object,
  displayName: PropTypes.string
}

export default WealthboxListRenderer
